import { useCallback, useEffect, useMemo } from 'react'

import { resolveStoreHours } from '@/features/store/lib/storeHours'
import { getConfig } from '@/shared/config/rewardsShop'
import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useRewardsStore } from '@/shared/store/useRewardsStore'
import type { RewardWithUnderscores } from '@/types/reward'

import { useStoreNudgeGate } from './useStoreNudgeGate'
import {
  buildGetProgress,
  deriveStoreNudgeCandidates,
  seedSeenStateFromCurrent
} from '../lib/deriveStoreNudgeCandidates'
import { initialState, nextTimer } from '../lib/storeNudgeMachine'
import { readSeenState, storageKey, writeSeenState } from '../model/storage'
import { useStoreNudgeStore } from '../model/store'


import type { StoreNudgeCatalogItem, StoreNudgeRewardRequest } from '../lib/isRedeemableNow'
import type { StoreNudgeBatch, StoreNudgeSeenState } from '../model/types'

export type UseStoreNudgeResult = {
  batch: StoreNudgeBatch | null
  open: boolean
  dismiss: () => void
  goToStore: () => void
}

const toSet = (ids: number[]) => new Set(ids)

/**
 * Orquestra o nudge da lojinha: cruza progresso e catálogo, persiste o que já
 * foi anunciado e conduz a máquina de tempo. Toda decisão vive nos módulos
 * puros de `lib/`; aqui só há ligação de hooks, timers e storage.
 */
export const useStoreNudge = (): UseStoreNudgeResult => {
  const {
    userProgress,
    wallet,
    finishedJourneys,
    finishedMissions,
    finishedLevels,
    finishedPartners
  } = useUserProgress()
  const contexto = useDigitalProductContextOptional()
  const digitalProduct = contexto?.digitalProduct
  const { navigateTo } = useNavigation()
  const gateOpen = useStoreNudgeGate()

  const userId = userProgress?.user?.id ?? null
  const ready = Boolean(userProgress && digitalProduct && userId)

  const { rewards, rewardItems, rewardRequests, isReady: catalogReady } =
    useRewardsStore({ enabled: ready })

  const { key, seen, machine, hydrate, setSeen, dispatch } = useStoreNudgeStore()

  const currentKey =
    digitalProduct && userId
      ? storageKey({ digitalProductId: digitalProduct.id, userId })
      : null

  // Chave textual: a identidade dos arrays de progresso não é garantida por
  // quem os produz, e a derivação abaixo não pode recalcular a cada render.
  const finishedKey = [finishedJourneys, finishedMissions, finishedLevels, finishedPartners]
    .map(ids => ids.join(','))
    .join('|')
  const finished = useMemo(
    () => {
      const [journeys, missions, levels, partners] = finishedKey
        .split('|')
        .map(part => (part ? part.split(',').map(Number) : []))
      return {
        journeys: toSet(journeys),
        missions: toSet(missions),
        levels: toSet(levels),
        partners: toSet(partners)
      }
    },
    [finishedKey]
  )

  const storeStatus = useMemo(
    () =>
      resolveStoreHours({
        openAt: digitalProduct?.reward_shop_open_at,
        closeAt: digitalProduct?.reward_shop_close_at
      }).status,
    [digitalProduct?.reward_shop_open_at, digitalProduct?.reward_shop_close_at]
  )

  // Hidrata uma vez por chave, e só com o catálogo carregado: o seed da
  // primeira execução precisa ver os rewards já destravados para não anunciar
  // o passado; com catálogo vazio ele marcaria nada e o backlog viria depois.
  useEffect(() => {
    if (!currentKey || !catalogReady || key === currentKey) return
    const stored = readSeenState(currentKey)
    const seeded =
      stored ??
      seedSeenStateFromCurrent(
        rewards as RewardWithUnderscores[],
        buildGetProgress(finished)
      )
    if (!stored) writeSeenState(currentKey, seeded)
    hydrate({
      key: currentKey,
      seen: seeded,
      machine: initialState({
        lastShownAt: seeded.lastShownAt,
        now: Date.now(),
        gateOpen
      })
    })
  }, [currentKey, catalogReady, key, rewards, finished, gateOpen, hydrate])

  const hydrated = key !== null && key === currentKey && seen !== null

  const derivation = useMemo(() => {
    if (!hydrated || !catalogReady || !seen) return null
    return deriveStoreNudgeCandidates({
      coins: Number(wallet?.coins ?? 0),
      finished,
      rewards: rewards as RewardWithUnderscores[],
      rewardItems: rewardItems as StoreNudgeCatalogItem[],
      rewardRequests: rewardRequests as StoreNudgeRewardRequest[],
      storeStatus,
      seen
    })
  }, [hydrated, catalogReady, seen, wallet?.coins, finished, rewards, rewardItems, rewardRequests, storeStatus])

  useEffect(() => {
    if (!derivation) return
    dispatch({ type: 'CANDIDATES', derivation, now: Date.now() })
  }, [derivation, dispatch])

  useEffect(() => {
    dispatch({ type: 'GATE_CHANGED', open: gateOpen, now: Date.now() })
  }, [gateOpen, dispatch])

  useEffect(() => {
    if (!machine) return undefined
    const timer = nextTimer(machine, Date.now())
    if (!timer) return undefined
    const handle = setTimeout(
      () => dispatch({ type: timer.event, now: Date.now() }),
      timer.delayMs
    )
    return () => clearTimeout(handle)
  }, [machine, dispatch])

  const batch = machine?.phase === 'showing' ? machine.batch : null

  const close = useCallback(() => {
    if (!batch || !seen || !key) return
    const nextSeen: StoreNudgeSeenState = {
      affordAnnounced: seen.affordAnnounced || batch.affordable !== null,
      announcedRewardIds: Array.from(
        new Set([...seen.announcedRewardIds, ...batch.unlocked.map(u => u.reward.id)])
      ),
      lastShownAt: new Date().toISOString()
    }
    writeSeenState(key, nextSeen)
    setSeen(nextSeen)
    dispatch({ type: 'DISMISS', now: Date.now() })
  }, [batch, seen, key, setSeen, dispatch])

  const goToStore = useCallback(() => {
    close()
    navigateTo(getConfig({ digitalProductId: contexto?.digitalProductId }).basePath)
  }, [close, navigateTo, contexto?.digitalProductId])

  return { batch, open: batch !== null, dismiss: close, goToStore }
}
